import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";

// ============================================================================
// COVERPAGE — Front/Back Cover PDF Composer
// Route: /offset-cover
//
// Same idea as the Pagebind book composer, but built for cover pages:
// images sit edge-to-edge with ZERO gap between them and ZERO outer margin —
// a true full-bleed layout. There is no spacing control at all, because
// covers must never show a gap between photos or a white border at the trim.
// ============================================================================

// ---- Page trim sizes (mm) --------------------------------------------------
const PAGE_SIZES = {
  portrait:  { w: 457, h: 635, label: "457 × 635 mm Portrait (635 × 457 mm H × W)" },
  landscape: { w: 635, h: 457, label: "635 × 457 mm Landscape" },
};

// ---- Fractional (0-1) layout templates per images-per-page ----------------
// Identical grid geometry to Pagebind — only the spacing model changes.
const LAYOUT_CACHE = new Map();
function getEqualGridLayout(count, pageAspect = 635 / 457) {
  const n = Math.max(1, Math.min(2000, Number(count) || 1));
  const cacheKey = `${n}:${Number(pageAspect).toFixed(4)}`;
  if (LAYOUT_CACHE.has(cacheKey)) return LAYOUT_CACHE.get(cacheKey);
  let best = { cols: 1, rows: n, score: Infinity };
  // Use only exact factor pairs so every grid position is occupied.
  // A partial final row (for example 18 items in a 4 × 5 grid) leaves
  // visually empty cells even though the selected count is correct.
  for (let cols = 1; cols <= n; cols++) {
    if (n % cols !== 0) continue;
    const rows = n / cols;
    const score = Math.abs(Math.log((pageAspect / cols) / (1 / rows)));
    if (score < best.score) best = { cols, rows, score };
  }
  const { cols, rows } = best;
  const layout = Array.from({ length: n }, (_, i) => {
    const row = Math.floor(i / cols), col = i % cols;
    return { x: col / cols, y: row / rows, w: 1 / cols, h: 1 / rows };
  });
  LAYOUT_CACHE.set(cacheKey, layout);
  return layout;
}


// Small blue cutting ticks matching the Books composer: marks at the ends of
// each internal grid boundary only; no full-length cutting lines.
const CUT_MARK_COLOR = "#1e3a8a";
const CUT_MARK_RGB = [30, 58, 138];
const CUT_MARK_WIDTH_MM = 0.6;
const CUT_TICK_LENGTH_MM = 5;
function getCoverCutCoordinates(perPage, pageW, pageH, extensionX, extensionY, outerMargin) {
  const layout = getEqualGridLayout(perPage, pageW / pageH);
  const xBounds = [...new Set(layout.flatMap((t) => [t.x, t.x + t.w]))].sort((a, b) => a - b);
  const yBounds = [...new Set(layout.flatMap((t) => [t.y, t.y + t.h]))].sort((a, b) => a - b);
  const baseW = Math.max(1, pageW - extensionX);
  const baseH = Math.max(1, pageH - extensionY);
  const usableW = Math.max(1, baseW - 2 * outerMargin);
  const usableH = Math.max(1, baseH - 2 * outerMargin);
  const extraX = xBounds.length > 1 ? extensionX / (xBounds.length - 1) : 0;
  const extraY = yBounds.length > 1 ? extensionY / (yBounds.length - 1) : 0;
  return {
    xs: xBounds.slice(1, -1).map((v, i) => outerMargin + v * usableW + (i + 1) * extraX),
    ys: yBounds.slice(1, -1).map((v, i) => outerMargin + v * usableH + (i + 1) * extraY),
  };
}
function drawCoverCutTicksCanvas(ctx, xs, ys, left, top, right, bottom, length, width) {
  ctx.save(); ctx.strokeStyle = CUT_MARK_COLOR; ctx.lineWidth = width; ctx.setLineDash([]);
  xs.forEach((x) => {
    ctx.beginPath(); ctx.moveTo(x, top); ctx.lineTo(x, top + length); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x, bottom - length); ctx.lineTo(x, bottom); ctx.stroke();
  });
  ys.forEach((y) => {
    ctx.beginPath(); ctx.moveTo(left, y); ctx.lineTo(left + length, y); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(right - length, y); ctx.lineTo(right, y); ctx.stroke();
  });
  ctx.restore();
}
function drawCoverCutTicksPdf(pdf, xs, ys, left, top, right, bottom, length) {
  pdf.setDrawColor(...CUT_MARK_RGB); pdf.setLineWidth(CUT_MARK_WIDTH_MM); pdf.setLineDashPattern([], 0);
  xs.forEach((x) => { pdf.line(x, top, x, top + length); pdf.line(x, bottom - length, x, bottom); });
  ys.forEach((y) => { pdf.line(left, y, left + length, y); pdf.line(right - length, y, right, y); });
}

// Which slot index is the "spine" boundary for 2-up front/back covers.
// Used only to label slots as Back / Front when perPage === 2.
const COVER_LABELS_2UP = ["Back Cover", "Front Cover"];

// ---- File / image helpers --------------------------------------------------
const readFileAsDataURL = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = (e) => res(e.target.result);
    r.onerror = () => rej(new Error("Read failed"));
    r.readAsDataURL(file);
  });

const loadImageObj = (src) =>
  new Promise((res, rej) => {
    const img = new window.Image();
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
    if (img.complete && img.naturalWidth) res(img);
  });

let jsPDFPromise = null;
function loadJsPDF() {
  if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve(window.jspdf.jsPDF);
  if (!jsPDFPromise) {
    jsPDFPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
      s.onload = () => resolve(window.jspdf.jsPDF);
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return jsPDFPromise;
}

// Contain-fit box math with independent common / horizontal / vertical zoom.
function computeSlotDraw(
  natW, natH,
  boxX, boxY, boxW, boxH,
  zoom, zoomX, zoomY,
  offX, offY
) {
  const coverScale = Math.max(boxW / natW, boxH / natH);

  const dw = natW * coverScale * zoom * zoomX;
const dh = natH * coverScale * zoom * zoomY;

  const maxPanX = Math.max(0, (dw - boxW) / 2);
  const maxPanY = Math.max(0, (dh - boxH) / 2);

  const baseX = boxX + (boxW - dw) / 2;
  const baseY = boxY + (boxH - dh) / 2;

  return {
    dw,
    dh,
    drawX: baseX + offX * maxPanX,
    drawY: baseY + offY * maxPanY,
    maxPanX,
    maxPanY,
  };
}

// ---- Slot geometry — adjustable outer margin and internal gaps --------------
// Outside margins are equal on all four edges. Internal gaps are twice the
// selected outer margin; half of each gap is inset from each neighbouring slot.
// Extension is distributed evenly across the distinct grid rows/columns.
function slotRectPx(template, pagePxW, pagePxH, perPage, extensionX = 0, extensionY = 0, outerPx = 0, gapPx = 0) {
  const baseW = Math.max(1, pagePxW - extensionX);
  const baseH = Math.max(1, pagePxH - extensionY);
  const usableW = Math.max(1, baseW - 2 * outerPx);
  const usableH = Math.max(1, baseH - 2 * outerPx);
  const layout = getEqualGridLayout(perPage, pagePxW / pagePxH);
  const xBounds = [...new Set(layout.flatMap((t) => [t.x, t.x + t.w]))].sort((a, b) => a - b);
  const yBounds = [...new Set(layout.flatMap((t) => [t.y, t.y + t.h]))].sort((a, b) => a - b);
  const xStart = Math.max(0, xBounds.findIndex((v) => Math.abs(v - template.x) < 1e-8));
  const xEnd = Math.max(xStart + 1, xBounds.findIndex((v) => Math.abs(v - (template.x + template.w)) < 1e-8));
  const yStart = Math.max(0, yBounds.findIndex((v) => Math.abs(v - template.y) < 1e-8));
  const yEnd = Math.max(yStart + 1, yBounds.findIndex((v) => Math.abs(v - (template.y + template.h)) < 1e-8));
  const extraPerColumn = xBounds.length > 1 ? extensionX / (xBounds.length - 1) : 0;
  const extraPerRow = yBounds.length > 1 ? extensionY / (yBounds.length - 1) : 0;
  const leftInset = xStart > 0 ? gapPx / 2 : 0;
  const rightInset = xEnd < xBounds.length - 1 ? gapPx / 2 : 0;
  const topInset = yStart > 0 ? gapPx / 2 : 0;
  const bottomInset = yEnd < yBounds.length - 1 ? gapPx / 2 : 0;
  return {
    x: outerPx + template.x * usableW + xStart * extraPerColumn + leftInset,
    y: outerPx + template.y * usableH + yStart * extraPerRow + topInset,
    w: Math.max(1, template.w * usableW + (xEnd - xStart) * extraPerColumn - leftInset - rightInset),
    h: Math.max(1, template.h * usableH + (yEnd - yStart) * extraPerRow - topInset - bottomInset),
  };
}

// ---- Draw one full page onto a canvas -------------------------------------
function renderPage(ctx, pagePxW, pagePxH, perPage, pageImages, selectedId, natSizeCache, extensionX = 0, extensionY = 0, outerPx = 0, gapPx = 0, showCutMarks = true, ppm = 3.2) {
  ctx.clearRect(0, 0, pagePxW, pagePxH);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, pagePxW, pagePxH);

  const template = getEqualGridLayout(perPage, pagePxW / pagePxH);
  template.forEach((t, i) => {
    const rect = slotRectPx(t, pagePxW, pagePxH, perPage, extensionX, extensionY, outerPx, gapPx);
    const entry = pageImages[i];

    if (!entry) {
      ctx.fillStyle = "#f4f1ea";
      ctx.fillRect(rect.x, rect.y, rect.w, rect.h);
      ctx.strokeStyle = "#d8d0bd";
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(rect.x + 1, rect.y + 1, rect.w - 2, rect.h - 2);
      ctx.setLineDash([]);
      ctx.fillStyle = "#a89f8a";
      ctx.font = `${Math.max(10, rect.w * 0.06)}px Georgia, serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("empty", rect.x + rect.w / 2, rect.y + rect.h / 2);
      return;
    }

    const nat = natSizeCache.get(entry.id);
    if (!nat) return;

    ctx.save();
    ctx.beginPath();
    ctx.rect(rect.x, rect.y, rect.w, rect.h);
    ctx.clip();
    const { dw, dh, drawX, drawY } = computeSlotDraw(
      nat.w, nat.h, rect.x, rect.y, rect.w, rect.h,
      entry.zoom, entry.zoomX, entry.zoomY,
      entry.offX, entry.offY
    );
    ctx.drawImage(nat.imgObj, drawX, drawY, dw, dh);
    ctx.restore();

    if (entry.id === selectedId) {
      ctx.strokeStyle = "#c9962f";
      ctx.lineWidth = Math.max(2, rect.w * 0.006);
      ctx.strokeRect(rect.x, rect.y, rect.w, rect.h);
    }
    // Note: no faint divider is drawn between non-selected slots on purpose —
    // covers must show zero seam between adjacent photos.
  });

  if (showCutMarks) {
    const { xs, ys } = getCoverCutCoordinates(perPage, pagePxW, pagePxH, extensionX, extensionY, outerPx);
    drawCoverCutTicksCanvas(ctx, xs, ys, outerPx, outerPx, pagePxW - outerPx, pagePxH - outerPx,
      CUT_TICK_LENGTH_MM * ppm, Math.max(2, pagePxW * 0.0016));
  }
}


// ---- Print-size fit calculator and saved manual sizes -----------------------
const PRINT_SIZE_PRESETS = {
  A4: { w: 210, h: 297 },
  A5: { w: 148, h: 210 },
  A6: { w: 105, h: 148 },
};
const SAVED_PRINT_SIZES_KEY = "pitics-offset-saved-print-sizes-v1";
function getFitCount(sheetW, sheetH, itemW, itemH) {
  if (![sheetW, sheetH, itemW, itemH].every((n) => Number.isFinite(n) && n > 0)) return 1;
  const normal = Math.floor(sheetW / itemW) * Math.floor(sheetH / itemH);
  const rotated = Math.floor(sheetW / itemH) * Math.floor(sheetH / itemW);
  return Math.max(1, normal, rotated);
}
function loadSavedPrintSizes() {
  try { return JSON.parse(localStorage.getItem(SAVED_PRINT_SIZES_KEY) || "[]"); }
  catch (_) { return []; }
}
function savePrintSize(name, w, h, existing) {
  const cleanName = String(name || "").trim();
  const width = Number(w), height = Number(h);
  if (!cleanName || !(width > 0) || !(height > 0)) return existing;
  const next = [...existing.filter((x) => x.name.toLowerCase() !== cleanName.toLowerCase()), { name: cleanName, w: width, h: height }];
  localStorage.setItem(SAVED_PRINT_SIZES_KEY, JSON.stringify(next));
  return next;
}
function deleteSavedPrintSize(name, existing) {
  const next = existing.filter((x) => x.name !== name);
  localStorage.setItem(SAVED_PRINT_SIZES_KEY, JSON.stringify(next));
  return next;
}

// ---- Small UI atoms ---------------------------------------------------------
function Label({ children }) {
  return (
    <label style={{
      display: "block", fontSize: 10.5, letterSpacing: "0.14em",
      color: "#a08f6e", marginBottom: 7, textTransform: "uppercase",
      fontFamily: "'Iowan Old Style','Georgia',serif",
    }}>{children}</label>
  );
}

function Hint({ children }) {
  return <p style={{ fontSize: 11, color: "#7a705c", marginTop: 5, lineHeight: 1.5 }}>{children}</p>;
}

function Select({ value, onChange, children }) {
  return (
    <select value={value} onChange={onChange} style={{
      width: "100%", background: "#1a1611", border: "1px solid #382f21",
      borderRadius: 6, color: "#f1ece1", padding: "9px 12px", fontSize: 13,
      cursor: "pointer", fontFamily: "'DM Mono','Fira Mono',monospace",
    }}>{children}</select>
  );
}

function ProgressBar({ value, total, label }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 11, color: "#a08f6e" }}>{label}</span>
        <span style={{ fontSize: 11, color: "#c9962f" }}>{value} / {total} ({pct}%)</span>
      </div>
      <div style={{ background: "#241e15", borderRadius: 4, height: 6, overflow: "hidden" }}>
        <div style={{
          width: `${pct}%`, height: "100%",
          background: "linear-gradient(90deg,#a8781e,#e0ab45)",
          transition: "width 0.2s", borderRadius: 4,
        }} />
      </div>
    </div>
  );
}

function Thumbnail({ src, index, selected, onSelect, onRemove }) {
  return (
    <div
      onClick={() => onSelect(index)}
      style={{
        position: "relative", width: 60, height: 60,
        border: `2px solid ${selected ? "#c9962f" : "#2c2418"}`,
        borderRadius: 5, overflow: "hidden", cursor: "pointer",
        flexShrink: 0, background: "#14110c",
      }}
    >
      <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(index); }}
        style={{
          position: "absolute", top: 1, right: 1,
          background: "rgba(0,0,0,0.75)", border: "none",
          borderRadius: 3, color: "#f18b6d", fontSize: 10, lineHeight: 1,
          padding: "1px 3px", cursor: "pointer", fontFamily: "inherit",
        }}
      >×</button>
      <div style={{
        position: "absolute", bottom: 1, left: 2,
        fontSize: 9, color: "rgba(255,255,255,0.55)", userSelect: "none",
      }}>{index + 1}</div>
    </div>
  );
}

function ZoomControl({ label, value, onMinus, onPlus }) {
  const btnStyle = {
    width: 36, height: 32, background: "#1a1611", border: "1px solid #382f21",
    borderRadius: 5, color: "#e0ab45", cursor: "pointer",
    fontFamily: "'DM Mono','Fira Mono',monospace", fontSize: 18, fontWeight: 700, lineHeight: 1,
  };

  return (
    <div style={{ marginTop: 10 }}>
      <Label>{label}</Label>
      <div style={{ display: "grid", gridTemplateColumns: "36px 1fr 36px", gap: 7, alignItems: "center" }}>
        <button type="button" onClick={onMinus} style={btnStyle}>−</button>
        <div style={{
          height: 32, display: "flex", alignItems: "center", justifyContent: "center",
          border: "1px solid #2c2418", borderRadius: 5, background: "#0f0c08",
          color: "#d8cdb4", fontSize: 12,
        }}>{value.toFixed(2)}×</div>
        <button type="button" onClick={onPlus} style={btnStyle}>+</button>
      </div>
    </div>
  );
}

function NavBtn({ onClick, disabled, children }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? "transparent" : "#1a1611",
      border: `1px solid ${disabled ? "#241e15" : "#382f21"}`,
      borderRadius: 5, color: disabled ? "#241e15" : "#c4b697",
      padding: "4px 10px", fontSize: 11, cursor: disabled ? "default" : "pointer",
      fontFamily: "'DM Mono','Fira Mono',monospace", transition: "all 0.15s",
    }}>{children}</button>
  );
}

// ============================================================================
// Main component
// ============================================================================
export default function OffsetCoverPage() {
  const [pageKey, setPageKey]         = useState("portrait");
  const [perPage, setPerPage]         = useState(2); // 2 = Back Cover + Front Cover, edge to edge
  const [fitChoice, setFitChoice] = useState("none");
  const [manualW, setManualW] = useState("210");
  const [manualH, setManualH] = useState("297");
  const [manualName, setManualName] = useState("");
  const [savedPrintSizes, setSavedPrintSizes] = useState(() => loadSavedPrintSizes());
  const [canvasMode, setCanvasMode]   = useState("normal");
  const [extensionDirection, setExtensionDirection] = useState("vertical");
  const [extensionMM, setExtensionMM] = useState(0);
  const [outerMarginMM, setOuterMarginMM] = useState(0);
  const [innerGapMM, setInnerGapMM] = useState(0);
  const [fillAllGrids, setFillAllGrids] = useState(false);
  const [applyAdjustmentsToAll, setApplyAdjustmentsToAll] = useState(false);
  const [showCutMarks, setShowCutMarks] = useState(true);

  const [images, setImages]           = useState([]); // {id,name,dataUrl,zoom,zoomX,zoomY,offX,offY}
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedId, setSelectedId]   = useState(null);

  const [isDragging, setIsDragging]   = useState(false); // file drag-over
  const [generating, setGenerating]   = useState(false);
  const [progress, setProgress]       = useState({ done: 0, total: 0 });

  const canvasRef    = useRef(null);
  const fileInputRef = useRef(null);
  const natCache     = useRef(new Map()); // id -> { imgObj, w, h }
  const panState     = useRef(null);      // active drag info

  const { w: basePageW, h: basePageH } = PAGE_SIZES[pageKey];
  const appliedExtensionMM = canvasMode === "extended" ? extensionMM : 0;
  const extensionXMM = extensionDirection === "horizontal" || extensionDirection === "both" ? appliedExtensionMM : 0;
  const extensionYMM = extensionDirection === "vertical" || extensionDirection === "both" ? appliedExtensionMM : 0;
  const pageW = basePageW + extensionXMM;
  const pageH = basePageH + extensionYMM;
  const activePrintSize = fitChoice === "A4" || fitChoice === "A5" || fitChoice === "A6"
    ? PRINT_SIZE_PRESETS[fitChoice]
    : savedPrintSizes.find((x) => `saved:${x.name}` === fitChoice) || (fitChoice === "manual" ? { w: Number(manualW), h: Number(manualH) } : null);
  const fitCount = activePrintSize ? getFitCount(pageW, pageH, activePrintSize.w, activePrintSize.h) : perPage;
  const activeLayout = useMemo(() => getEqualGridLayout(perPage, pageW / pageH), [perPage, pageW, pageH]);
  const applyPrintSize = (choice) => {
    setFitChoice(choice);
    const size = PRINT_SIZE_PRESETS[choice] || savedPrintSizes.find((x) => `saved:${x.name}` === choice) || (choice === "manual" ? { w: Number(manualW), h: Number(manualH) } : null);
    if (size) setPerPage(getFitCount(pageW, pageH, size.w, size.h));
  };
  const saveManualPrintSize = () => {
    const next = savePrintSize(manualName, manualW, manualH, savedPrintSizes);
    if (next !== savedPrintSizes) {
      setSavedPrintSizes(next);
      setFitChoice(`saved:${String(manualName).trim()}`);
      setPerPage(getFitCount(pageW, pageH, Number(manualW), Number(manualH)));
    }
  };
  const removeManualPrintSize = (name) => {
    const next = deleteSavedPrintSize(name, savedPrintSizes);
    setSavedPrintSizes(next);
    if (fitChoice === `saved:${name}`) {
      setFitChoice("none");
      setPerPage(2);
    }
  };
  useEffect(() => {
    if (activePrintSize) setPerPage(getFitCount(pageW, pageH, activePrintSize.w, activePrintSize.h));
  }, [pageW, pageH, fitChoice, manualW, manualH, savedPrintSizes]);
  const totalPages = Math.max(1, Math.ceil(images.length / perPage) || 1);

  useEffect(() => {
    if (currentPage > totalPages - 1) setCurrentPage(Math.max(0, totalPages - 1));
  }, [totalPages, currentPage]);

  const pages = useMemo(() => {
    const chunks = [];
    for (let i = 0; i < images.length; i += perPage) chunks.push(images.slice(i, i + perPage));
    if (chunks.length === 0) chunks.push([]);
    return chunks;
  }, [images, perPage]);

  const currentPageImages = pages[currentPage] || [];

  // ---- Load natural sizes for any new images --------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const img of images) {
        if (!natCache.current.has(img.id)) {
          try {
            const obj = await loadImageObj(img.dataUrl);
            if (cancelled) return;
            natCache.current.set(img.id, { imgObj: obj, w: obj.naturalWidth, h: obj.naturalHeight });
            setImages((prev) => [...prev]); // trigger repaint
          } catch (_) { /* ignore broken image */ }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [images]);

  // ---- Preview canvas ---------------------------------------------------------
  const PPM = 3.2;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pxW = Math.round(pageW * PPM);
    const pxH = Math.round(pageH * PPM);
    canvas.width = pxW;
    canvas.height = pxH;
    const ctx = canvas.getContext("2d");
    renderPage(ctx, pxW, pxH, perPage, currentPageImages, selectedId, natCache.current, Math.round(extensionXMM * PPM), Math.round(extensionYMM * PPM), outerMarginMM * PPM, innerGapMM * PPM, showCutMarks, PPM);
  }, [pageW, pageH, perPage, currentPageImages, selectedId, images, extensionXMM, extensionYMM, outerMarginMM, innerGapMM, showCutMarks]);

  // ---- Upload handling ---------------------------------------------------------
  const addFiles = useCallback(async (files) => {
    const imgFiles = Array.from(files).filter((f) => f.type.startsWith("image/")).slice(0, 500);
    const entries = await Promise.all(imgFiles.map(async (f, idx) => ({
      id: `img_${Date.now()}_${idx}_${Math.random().toString(36).slice(2, 7)}`,
      name: f.name,
      dataUrl: await readFileAsDataURL(f),
      zoom: 1, zoomX: 1, zoomY: 1, offX: 0, offY: 0,
    })));
    setImages((prev) => {
      const incoming = fillAllGrids && entries.length === 1
        ? Array.from({ length: Math.max(1, perPage) }, (_, i) => ({
            ...entries[0],
            id: `${entries[0].id}_grid_${i + 1}`,
            name: entries[0].name,
          }))
        : entries;
      return [...prev, ...incoming].slice(0, 2000);
    });
  }, [fillAllGrids, perPage]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(e.dataTransfer.files);
  }, [addFiles]);

  // ---- Selection + drag-to-pan on the preview canvas ---------------------------
  const findSlotAtPoint = useCallback((px, py) => {
    const pxW = Math.round(pageW * PPM);
    const pxH = Math.round(pageH * PPM);
    const template = getEqualGridLayout(perPage, pxW / pxH);
    for (let i = 0; i < template.length; i++) {
      const rect = slotRectPx(template[i], pxW, pxH, perPage, Math.round(extensionXMM * PPM), Math.round(extensionYMM * PPM), outerMarginMM * PPM, innerGapMM * PPM);
      if (px >= rect.x && px <= rect.x + rect.w && py >= rect.y && py <= rect.y + rect.h) {
        return { index: i, rect };
      }
    }
    return null;
  }, [pageW, pageH, perPage, extensionXMM, extensionYMM, outerMarginMM, innerGapMM]);

  const canvasPointFromEvent = (e) => {
    const canvas = canvasRef.current;
    const bounds = canvas.getBoundingClientRect();
    const scaleX = canvas.width / bounds.width;
    const scaleY = canvas.height / bounds.height;
    return {
      x: (e.clientX - bounds.left) * scaleX,
      y: (e.clientY - bounds.top) * scaleY,
    };
  };

  const onCanvasMouseDown = (e) => {
    const pt = canvasPointFromEvent(e);
    const hit = findSlotAtPoint(pt.x, pt.y);
    if (!hit) return;
    const entry = currentPageImages[hit.index];
    if (!entry) return;
    setSelectedId(entry.id);
    const nat = natCache.current.get(entry.id);
    if (!nat) return;
    const { maxPanX, maxPanY } = computeSlotDraw(
      nat.w, nat.h, hit.rect.x, hit.rect.y, hit.rect.w, hit.rect.h,
      entry.zoom, entry.zoomX, entry.zoomY,
      entry.offX, entry.offY
    );
    panState.current = {
      id: entry.id, startX: pt.x, startY: pt.y,
      startOffX: entry.offX, startOffY: entry.offY,
      maxPanX, maxPanY,
    };
  };

  const onCanvasMouseMove = (e) => {
    if (!panState.current) return;
    const pt = canvasPointFromEvent(e);
    const { id, startX, startY, startOffX, startOffY, maxPanX, maxPanY } = panState.current;
    const dx = pt.x - startX;
    const dy = pt.y - startY;
    const nOffX = maxPanX > 0 ? clamp(startOffX + dx / maxPanX, -1, 1) : 0;
    const nOffY = maxPanY > 0 ? clamp(startOffY + dy / maxPanY, -1, 1) : 0;
    updateImage(id, { offX: nOffX, offY: nOffY });
  };

  const endPan = () => { panState.current = null; };

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  const updateImage = (id, patch) => {
    setImages((prev) => prev.map((img) => (img.id === id ? { ...img, ...patch } : img)));
  };

  const ZOOM_STEP = 0.01;
  const MIN_ZOOM = 0.25;
  const MAX_ZOOM = 4;

  const adjustZoom = (id, key, delta) => {
    setImages((prev) =>
      prev.map((img) => {
        if (!applyAdjustmentsToAll && img.id !== id) return img;
        const current = img[key] ?? 1;
        return {
          ...img,
          [key]: clamp(Math.round((current + delta) * 100) / 100, MIN_ZOOM, MAX_ZOOM),
        };
      })
    );
  };

  const selectedImage = images.find((i) => i.id === selectedId) || null;

  // ---- PDF generation -----------------------------------------------------------
  // Export uses the same adjustable margins, gaps, and extension geometry as the preview.
  const handleGenerate = async () => {
    if (images.length === 0) return;

    setGenerating(true);
    setProgress({ done: 0, total: pages.length });
    await new Promise((r) => setTimeout(r, 30));

    try {
      const jsPDF = await loadJsPDF();
      const orientation = pageW > pageH ? "l" : "p";
      const pdf = new jsPDF({
        orientation,
        unit: "mm",
        format: [pageW, pageH],
        compress: true,
      });

      for (let p = 0; p < pages.length; p++) {
        if (p > 0) {
          pdf.addPage([pageW, pageH], orientation);
        }

        const pageImages = pages[p];
        const template = getEqualGridLayout(perPage, pageW / pageH);

        for (let i = 0; i < template.length; i++) {
          const entry = pageImages[i];
          if (!entry) continue;

          const nat = natCache.current.get(entry.id);
          if (!nat) continue;

          const rect = slotRectPx(template[i], pageW, pageH, perPage, extensionXMM, extensionYMM, outerMarginMM, innerGapMM);

          const draw = computeSlotDraw(
            nat.w, nat.h, rect.x, rect.y, rect.w, rect.h,
            entry.zoom, entry.zoomX, entry.zoomY,
            entry.offX, entry.offY
          );

          // Clip to the slot only — never stroke the clip path into the PDF.
          pdf.saveGraphicsState();
          pdf.rect(rect.x, rect.y, rect.w, rect.h, null);
          pdf.clip();
          pdf.discardPath();

          let format = "JPEG";
          if (entry.dataUrl.startsWith("data:image/png")) format = "PNG";
          else if (entry.dataUrl.startsWith("data:image/webp")) format = "WEBP";

          pdf.addImage(
            entry.dataUrl, format,
            draw.drawX, draw.drawY, draw.dw, draw.dh,
            undefined, "FAST"
          );

          pdf.restoreGraphicsState();
        }

        if (showCutMarks) {
          const { xs, ys } = getCoverCutCoordinates(
            perPage, pageW, pageH, extensionXMM, extensionYMM, outerMarginMM
          );
          drawCoverCutTicksPdf(
            pdf, xs, ys, outerMarginMM, outerMarginMM,
            pageW - outerMarginMM, pageH - outerMarginMM, CUT_TICK_LENGTH_MM
          );
        }

        setProgress({ done: p + 1, total: pages.length });
        await new Promise((r) => setTimeout(r, 0));
      }

      pdf.save(`cover_${pages.length}pages_${pageKey}.pdf`);
    } catch (err) {
      console.error(err);
      alert("Failed to generate PDF: " + err.message);
    } finally {
      setGenerating(false);
      setProgress({ done: 0, total: 0 });
    }
  };

  const mmToIn = (mm) => (mm / 25.4).toFixed(1);
  const canGen = images.length > 0 && !generating;

  const slotLabel = (i) => (perPage === 2 ? COVER_LABELS_2UP[i] : `Slot ${i + 1}`);

  // ---- Render -------------------------------------------------------------------
  return (
    <div style={{
      minHeight: "100vh", background: "#0e0b07",
      fontFamily: "'DM Mono','Fira Mono',monospace",
      color: "#f1ece1", display: "flex", flexDirection: "column",
    }}>
      <header style={{
        borderBottom: "1px solid #241e15", padding: "1.25rem 2rem",
        display: "flex", alignItems: "baseline", gap: "1rem",
      }}>
        <span style={{
          fontSize: 11, letterSpacing: "0.2em", color: "#a08f6e",
          textTransform: "uppercase",
        }}>/cover</span>
        <h1 style={{
          fontSize: 21, fontWeight: 400, margin: 0, color: "#f6efe1",
          letterSpacing: "-0.01em", fontFamily: "'Iowan Old Style','Georgia',serif",
        }}>
          Offset CoverPage — Full-Bleed Cover Composer
        </h1>
      </header>

      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "360px 1fr", overflow: "hidden" }}>
        {/* ---------------- Sidebar ---------------- */}
        <aside style={{
          borderRight: "1px solid #241e15", padding: "1.5rem",
          display: "flex", flexDirection: "column", gap: "1.25rem", overflowY: "auto",
        }}>
          {/* Page size */}
          <section>
            <Label>Page Size</Label>
            <Select value={pageKey} onChange={(e) => setPageKey(e.target.value)}>
              {Object.entries(PAGE_SIZES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </Select>
            <Hint>{pageW.toFixed(1)} × {pageH.toFixed(1)} mm ({mmToIn(pageW)} × {mmToIn(pageH)} in)</Hint>
          </section>

          {/* Standard / custom print size fit */}
          <section>
            <Label>Print Size to Fit on 635 × 457 Sheet</Label>
            <Select value={fitChoice} onChange={(e) => applyPrintSize(e.target.value)}>
              <option value="none">Custom grid (choose image count below)</option>
              <option value="A4">A4 — 210 × 297 mm</option>
              <option value="A5">A5 — 148 × 210 mm</option>
              <option value="A6">A6 — 105 × 148 mm</option>
              {savedPrintSizes.map((x) => <option key={x.name} value={`saved:${x.name}`}>{x.name} — {x.w} × {x.h} mm</option>)}
              <option value="manual">Manual size…</option>
            </Select>
            {fitChoice !== "none" && activePrintSize && <Hint>Maximum fit (including rotation): {getFitCount(pageW, pageH, activePrintSize.w, activePrintSize.h)} items per sheet. The sheet is divided into equal-sized grid cells.</Hint>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7, marginTop: 9 }}>
              <input aria-label="Manual item width in mm" type="number" min="1" step="0.1" value={manualW} onChange={(e) => { setManualW(e.target.value); if (fitChoice === "manual") setPerPage(getFitCount(pageW, pageH, Number(e.target.value), Number(manualH))); }} placeholder="Width (mm)" style={{ minWidth: 0, padding: 8, background: "#1a1611", color: "#f1ece1", border: "1px solid #382f21", borderRadius: 5 }} />
              <input aria-label="Manual item height in mm" type="number" min="1" step="0.1" value={manualH} onChange={(e) => { setManualH(e.target.value); if (fitChoice === "manual") setPerPage(getFitCount(pageW, pageH, Number(manualW), Number(e.target.value))); }} placeholder="Height (mm)" style={{ minWidth: 0, padding: 8, background: "#1a1611", color: "#f1ece1", border: "1px solid #382f21", borderRadius: 5 }} />
            </div>
            <input aria-label="Name for saved manual print size" value={manualName} onChange={(e) => setManualName(e.target.value)} placeholder="Your choice name (e.g. Planner)" style={{ width: "100%", boxSizing: "border-box", marginTop: 7, padding: 8, background: "#1a1611", color: "#f1ece1", border: "1px solid #382f21", borderRadius: 5 }} />
            <button type="button" onClick={() => { setFitChoice("manual"); setPerPage(getFitCount(pageW, pageH, Number(manualW), Number(manualH))); }} style={{ marginTop: 7, width: "100%", padding: 8, background: "#1a1611", color: "#e0ab45", border: "1px solid #8d6829", borderRadius: 5, cursor: "pointer" }}>Apply Manual W × H</button>
            <button type="button" disabled={!manualName.trim() || !(Number(manualW) > 0) || !(Number(manualH) > 0)} onClick={saveManualPrintSize} style={{ marginTop: 6, width: "100%", padding: 8, background: "#21190c", color: "#e0ab45", border: "1px solid #8d6829", borderRadius: 5, cursor: "pointer", opacity: !manualName.trim() || !(Number(manualW) > 0) || !(Number(manualH) > 0) ? 0.45 : 1 }}>Save Manual Size with Name</button>
            {savedPrintSizes.length > 0 && (
              <div style={{ marginTop: 10, padding: 9, border: "1px solid #382f21", borderRadius: 6, background: "#120e09" }}>
                <div style={{ fontSize: 10, color: "#a08f6e", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 7 }}>Saved Manual Sizes</div>
                {savedPrintSizes.map((item) => (
                  <div key={item.name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "5px 0", borderBottom: "1px solid #241e15" }}>
                    <span style={{ minWidth: 0, fontSize: 11, color: "#d8cdb4", overflowWrap: "anywhere" }}>{item.name} — {item.w} × {item.h} mm</span>
                    <button type="button" onClick={() => removeManualPrintSize(item.name)} aria-label={`Delete saved size ${item.name}`} style={{ flexShrink: 0, padding: "4px 8px", background: "#2a120e", color: "#f18b6d", border: "1px solid #713b2d", borderRadius: 4, cursor: "pointer", fontSize: 10 }}>Delete</button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Canvas extension */}
          <section>
            <Label>Canvas Mode</Label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {["normal", "extended"].map((mode) => (
                <button key={mode} type="button" onClick={() => setCanvasMode(mode)} style={{
                  background: canvasMode === mode ? "#241c11" : "transparent",
                  border: `1px solid ${canvasMode === mode ? "#c9962f" : "#2c2418"}`,
                  borderRadius: 6, color: canvasMode === mode ? "#e0ab45" : "#a08f6e",
                  padding: "9px", fontSize: 12, cursor: "pointer", textTransform: "capitalize",
                  fontFamily: "inherit",
                }}>{mode}</button>
              ))}
            </div>
            {canvasMode === "extended" && <>
              <Label>Extension Direction</Label>
              <Select value={extensionDirection} onChange={(e) => setExtensionDirection(e.target.value)}>
                <option value="vertical">Vertical</option>
                <option value="horizontal">Horizontal</option>
                <option value="both">Both</option>
              </Select>
              <Label>Extension Size: {extensionMM.toFixed(1)} mm</Label>
              <div style={{ display: "grid", gridTemplateColumns: "36px 1fr 36px", gap: 7, alignItems: "center" }}>
                <button type="button" onClick={() => setExtensionMM((v) => Math.max(0, Math.round((v - 0.5) * 10) / 10))} style={{ padding: 7, background: "#1a1611", border: "1px solid #382f21", borderRadius: 5, color: "#e0ab45" }}>−</button>
                <input aria-label="Extension size in millimetres" type="range" min="0" max="20" step="0.5" value={extensionMM} onChange={(e) => setExtensionMM(Number(e.target.value))} />
                <button type="button" onClick={() => setExtensionMM((v) => Math.min(20, Math.round((v + 0.5) * 10) / 10))} style={{ padding: 7, background: "#1a1611", border: "1px solid #382f21", borderRadius: 5, color: "#e0ab45" }}>+</button>
              </div>
              <Hint>Extra space is divided evenly across grid rows or columns. For example, an 8 mm vertical extension adds 4 mm to each row in a two-row layout. PDF export uses the extended page size.</Hint>
            </>}
          </section>

          {/* Images per page */}
          <section>
            <Label>Images Per Page</Label>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
              {[1, 2, 4, 6, 9].map((n) => (
                <button
                  key={n}
                  onClick={() => { setFitChoice("none"); setPerPage(n); }}
                  style={{
                    width: "100%", background: perPage === n ? "#241c11" : "transparent",
                    border: `1px solid ${perPage === n ? "#c9962f" : "#2c2418"}`,
                    borderRadius: 6, color: perPage === n ? "#e0ab45" : "#a08f6e",
                    padding: "10px 0", fontSize: 14, cursor: "pointer",
                    fontFamily: "inherit", fontWeight: 600, transition: "all 0.15s",
                  }}
                >{n}</button>
              ))}
            </div>
            <Hint>
              {perPage === 2
                ? "2-up: left slot is Back Cover, right slot is Front Cover — touching at the spine."
                : `${pages.length} page${pages.length !== 1 ? "s" : ""} total for ${images.length} image${images.length !== 1 ? "s" : ""}.`}
            </Hint>
          </section>

          {/* Adjustable outer margin and internal photo gaps */}
          <section style={{
            background: "#161009", border: "1px solid #2c2418", borderRadius: 8, padding: "0.9rem",
          }}>
            <p style={{
              fontSize: 10.5, letterSpacing: "0.14em", color: "#a08f6e",
              textTransform: "uppercase", margin: "0 0 8px",
              fontFamily: "'Iowan Old Style','Georgia',serif",
            }}>Space Adjustment</p>
            <p style={{ fontSize: 12, color: "#e0ab45", margin: "0 0 8px", fontWeight: 600 }}>
              Outside margin: {outerMarginMM.toFixed(1)} mm · Internal gaps: {innerGapMM.toFixed(1)} mm
            </p>
            <Label>Outer Page Margin (mm)</Label>
            <input
              type="range" min="0" max="30" step="0.5" value={outerMarginMM}
              aria-label="Outside page margin in millimetres"
              onChange={(e) => setOuterMarginMM(Number(e.target.value))}
              style={{ width: "100%", accentColor: "#c9962f" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#7a705c" }}>
              <span>0 mm — full bleed</span><span>30 mm</span>
            </div>
            <div style={{ marginTop: 12 }}>
              <Label>Inner Grid Gap (mm)</Label>
              <input
                type="range" min="0" max="30" step="0.5" value={innerGapMM}
                aria-label="Inner grid gap in millimetres"
                onChange={(e) => setInnerGapMM(Number(e.target.value))}
                style={{ width: "100%", accentColor: "#c9962f" }}
              />
              <Hint>Reduce the gap between neighbouring grids to leave more usable space for the outside borders. Preview and exported PDF use the same geometry.</Hint>
            </div>
            <Hint>Outside margins are equal on all four edges. Set both values to 0 mm for the original edge-to-edge cover.</Hint>
          </section>

          {/* Grid cutting marks */}
          <section style={{ background: "#161009", border: "1px solid #2c2418", borderRadius: 8, padding: "0.9rem" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={showCutMarks}
                onChange={(e) => setShowCutMarks(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: CUT_MARK_COLOR, cursor: "pointer" }}
              />
              <span style={{ fontSize: 10.5, letterSpacing: "0.14em", color: "#a08f6e", textTransform: "uppercase", fontFamily: "'Iowan Old Style','Georgia',serif" }}>
                Grid Cutting Marks
              </span>
              <span style={{ marginLeft: "auto", width: 22, height: 4, borderRadius: 2, background: CUT_MARK_COLOR, flexShrink: 0 }} />
            </label>
            <Hint>Tiny blue 5 mm ticks at the ends of each internal grid boundary only. No full-length lines. Shown in preview and included in the exported PDF.</Hint>
          </section>

          {/* Selected image adjust */}
          <section style={{
            background: "#161009", border: "1px solid #2c2418", borderRadius: 8, padding: "0.9rem",
          }}>
            <p style={{
              fontSize: 10.5, color: "#a08f6e", margin: "0 0 8px", textTransform: "uppercase",
              letterSpacing: "0.12em", fontFamily: "'Iowan Old Style','Georgia',serif",
            }}>Adjust Selected Photo</p>

            <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#d8cdb4", fontSize: 12, marginBottom: 10 }}>
              <input type="checkbox" checked={applyAdjustmentsToAll} onChange={(e) => setApplyAdjustmentsToAll(e.target.checked)} />
              Apply width / height / both adjustments to all grids
            </label>
            {selectedImage ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <img src={selectedImage.dataUrl} alt="" style={{
                    width: 40, height: 40, objectFit: "cover", borderRadius: 4,
                    border: "1px solid #382f21",
                  }} />
                  <span style={{ fontSize: 11, color: "#d8cdb4", wordBreak: "break-all" }}>
                    {selectedImage.name}
                  </span>
                </div>

                <ZoomControl
                  label="Common Zoom"
                  value={selectedImage.zoom}
                  onMinus={() => adjustZoom(selectedImage.id, "zoom", -ZOOM_STEP)}
                  onPlus={() => adjustZoom(selectedImage.id, "zoom", ZOOM_STEP)}
                />
                <Hint>Changes width and height equally in 0.01× steps.</Hint>

                <ZoomControl
                  label="Horizontal Zoom"
                  value={selectedImage.zoomX}
                  onMinus={() => adjustZoom(selectedImage.id, "zoomX", -ZOOM_STEP)}
                  onPlus={() => adjustZoom(selectedImage.id, "zoomX", ZOOM_STEP)}
                />
                <Hint>Changes only the image width in 0.01× steps.</Hint>

                <ZoomControl
                  label="Vertical Zoom"
                  value={selectedImage.zoomY}
                  onMinus={() => adjustZoom(selectedImage.id, "zoomY", -ZOOM_STEP)}
                  onPlus={() => adjustZoom(selectedImage.id, "zoomY", ZOOM_STEP)}
                />
                <Hint>Changes only the image height in 0.01× steps.</Hint>

                <button
                  onClick={() => updateImage(selectedImage.id, {
                    zoom: 1, zoomX: 1, zoomY: 1, offX: 0, offY: 0,
                  })}
                  style={{
                    marginTop: 8, width: "100%", background: "transparent",
                    border: "1px solid #382f21", borderRadius: 5,
                    color: "#a08f6e", padding: "7px 10px", fontSize: 11,
                    cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  Reset zoom & position
                </button>

                <Hint>
                  Use + / − to make the selected image use more or less slot space.
                  Enlarged images stay clipped to their own slot and cannot overlap neighbouring images.
                  Drag the image on the preview to reposition cropped areas.
                </Hint>
              </>
            ) : (
              <Hint>
                Click a photo on the page preview or its thumbnail to select it,
                then use Common, Horizontal or Vertical zoom.
              </Hint>
            )}
          </section>

          {/* Upload */}
          <section>
            <Label>Upload Images</Label>
            <div
              onClick={() => fileInputRef.current.click()}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              style={{
                border: `1.5px dashed ${isDragging ? "#c9962f" : images.length ? "#4a7a4f" : "#382f21"}`,
                borderRadius: 8, padding: "1.1rem", textAlign: "center", cursor: "pointer",
                transition: "all 0.2s", background: isDragging ? "#1c1408" : "transparent",
              }}
            >
              {images.length > 0 ? (
                <>
                  <p style={{ fontSize: 12, color: "#7fb583", margin: "0 0 4px" }}>
                    {images.length} image{images.length > 1 ? "s" : ""} loaded
                  </p>
                  <p style={{ fontSize: 11, color: "#7a705c", margin: 0 }}>Drop more or click to add</p>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 22, margin: "0 0 8px", color: "#382f21" }}>↑</p>
                  <p style={{ fontSize: 13, color: "#7a705c", margin: 0 }}>
                    Drop images or click to browse
                  </p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef} type="file" accept="image/*" multiple
              onChange={(e) => addFiles(e.target.files)}
              style={{ display: "none" }}
            />
            <label style={{ display: "flex", alignItems: "center", gap: 8, color: "#d8cdb4", fontSize: 12, marginTop: 9 }}>
              <input type="checkbox" checked={fillAllGrids} onChange={(e) => setFillAllGrids(e.target.checked)} />
              Fill all grids with one uploaded image
            </label>
            <Hint>When enabled, uploading a single image duplicates it into every grid on the current sheet layout.</Hint>

            {images.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <div style={{
                  display: "flex", gap: 6, flexWrap: "wrap",
                  maxHeight: 140, overflowY: "auto", padding: "4px 2px",
                }}>
                  {images.map((img, i) => (
                    <Thumbnail
                      key={img.id} src={img.dataUrl} index={i}
                      selected={img.id === selectedId}
                      onSelect={() => {
                        setSelectedId(img.id);
                        setCurrentPage(Math.floor(i / perPage));
                      }}
                      onRemove={(idx) => {
                        setImages((prev) => prev.filter((_, j) => j !== idx));
                        if (images[idx].id === selectedId) setSelectedId(null);
                      }}
                    />
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
                  <p style={{ fontSize: 11, color: "#7a705c", margin: 0 }}>
                    {images.length} image{images.length !== 1 ? "s" : ""} · {pages.length} page{pages.length !== 1 ? "s" : ""}
                  </p>
                  <button
                    onClick={() => { setImages([]); setSelectedId(null); natCache.current.clear(); }}
                    style={{
                      background: "transparent", border: "none", color: "#e07a5f",
                      fontSize: 11, cursor: "pointer", fontFamily: "inherit", padding: 0,
                    }}
                  >Clear all</button>
                </div>
              </div>
            )}

            {generating && progress.total > 0 && (
              <ProgressBar value={progress.done} total={progress.total} label="Rendering pages…" />
            )}
          </section>

          {/* Generate */}
          <button
            onClick={handleGenerate}
            disabled={!canGen}
            style={{
              background: canGen ? "#c9962f" : "#241e15",
              border: "none", borderRadius: 8,
              color: canGen ? "#150f08" : "#4a4030",
              padding: "13px", fontSize: 14, fontWeight: 700,
              cursor: canGen ? "pointer" : "not-allowed",
              fontFamily: "inherit", letterSpacing: "0.04em", transition: "all 0.2s",
            }}
          >
            {generating
              ? `Generating… (${progress.done}/${progress.total})`
              : images.length === 0
              ? "Upload images first"
              : `Download PDF (${pages.length} page${pages.length !== 1 ? "s" : ""})`}
          </button>
        </aside>

        {/* ---------------- Preview ---------------- */}
        <main style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: "2rem", background: "#080603", overflow: "auto",
        }}>
          <div style={{ textAlign: "center" }}>
            <p style={{
              fontSize: 11, color: "#4a4030", marginBottom: "0.9rem",
              letterSpacing: "0.12em", textTransform: "uppercase",
            }}>
              Page {currentPage + 1} of {pages.length} — {pageW.toFixed(0)} × {pageH.toFixed(0)} mm
              {perPage === 2 ? ` — ${COVER_LABELS_2UP.join(" / ")}` : ""}
            </p>

            {pages.length > 1 && (
              <div style={{ display: "flex", justifyContent: "center", gap: 8, marginBottom: 12 }}>
                <NavBtn onClick={() => setCurrentPage((p) => Math.max(0, p - 1))} disabled={currentPage === 0}>◀ Prev</NavBtn>
                <span style={{ fontSize: 12, color: "#7a705c", lineHeight: "30px" }}>
                  {currentPage + 1} / {pages.length}
                </span>
                <NavBtn onClick={() => setCurrentPage((p) => Math.min(pages.length - 1, p + 1))} disabled={currentPage === pages.length - 1}>Next ▶</NavBtn>
              </div>
            )}

            <div style={{
              boxShadow: "0 0 0 1px #241e15, 0 24px 70px rgba(0,0,0,0.85)",
              display: "inline-block", borderRadius: 3, overflow: "hidden",
            }}>
              <canvas
                ref={canvasRef}
                onMouseDown={onCanvasMouseDown}
                onMouseMove={onCanvasMouseMove}
                onMouseUp={endPan}
                onMouseLeave={endPan}
                style={{
                  display: "block", cursor: currentPageImages.length ? "grab" : "default",
                  maxWidth: "min(760px, 100%)", maxHeight: "72vh",
                  width: "auto", height: "auto",
                }}
              />
            </div>

            {images.length === 0 ? (
              <p style={{ fontSize: 12, color: "#382f21", marginTop: "1rem" }}>
                Upload photos to start composing your cover. Adjust the outside margin and internal gaps as needed.
              </p>
            ) : (
              <p style={{ fontSize: 11, color: "#4a4030", marginTop: "0.9rem" }}>
                Select a photo · use + / − Common, Horizontal or Vertical zoom · drag to reposition
              </p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
